import { ParticleSystem, DynamicTexture, Color4, Vector3 } from "@babylonjs/core";

const ENEMY_COLORS = {
    Traqueur:        new Color4(1.0, 0.45, 0.0, 1),
    Sentinelle:      new Color4(0.1, 0.6,  1.0, 1),
    SentinelleElite: new Color4(0.7, 0.1,  1.0, 1),
    Bombardier:      new Color4(1.0, 0.2,  0.0, 1),
    Pulse:           new Color4(1.0, 0.0,  0.9, 1),
    Tank:            new Color4(0.9, 0.6,  0.0, 1),
    NEXUS:           new Color4(0.0, 1.0,  1.0, 1),
};

export class VFXManager {
    constructor(scene, camera) {
        this.scene    = scene;
        this.camera   = camera;

        this._hitFlashEl    = document.getElementById("vfx-hit-flash");
        this._shakeFrames   = 0;
        this._shakeIntensity = 0;
        this._shakeBase     = 10;

        // Texture partagée entre tous les ParticleSystems (32×32 gradient radial)
        this._dotTex = this._buildDotTex();
    }

    _buildDotTex() {
        const tex = new DynamicTexture("vfx_dot", { width: 32, height: 32 }, this.scene, false);
        const ctx = tex.getContext();
        const g   = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
        g.addColorStop(0,   "rgba(255,255,255,1)");
        g.addColorStop(0.4, "rgba(255,255,255,0.6)");
        g.addColorStop(1,   "rgba(255,255,255,0)");
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, 32, 32);
        tex.update();
        return tex;
    }

    // ─── Boucle par frame ────────────────────────────────────────────────────

    update() {
        if (this._shakeFrames <= 0) return;
        const s = (Math.random() - 0.5) * this._shakeIntensity;
        this.camera.heightOffset = this._shakeBase + s;
        this._shakeFrames--;
        if (this._shakeFrames === 0) {
            this.camera.heightOffset = this._shakeBase;
        }
    }

    // ─── API publique ─────────────────────────────────────────────────────────

    /** Flash rouge + shake caméra quand YASSO est touché */
    playerHit() {
        if (this._hitFlashEl) {
            this._hitFlashEl.classList.remove("active");
            void this._hitFlashEl.offsetWidth;
            this._hitFlashEl.classList.add("active");
        }
        // Shake seulement si la caméra est en position de jeu (pas pendant l'intro)
        if (this.camera.heightOffset < 18) {
            this._shakeBase     = this.camera.heightOffset;
            this._shakeFrames   = 14;
            this._shakeIntensity = 0.7;
        }
    }

    /** Burst de propulsion cyan au démarrage du dash */
    dashBurst(position, direction) {
        const ps = this._makePS("dashBurst", 100);
        ps.emitter     = position.clone();
        ps.minEmitBox  = new Vector3(-0.2, -0.1, -0.2);
        ps.maxEmitBox  = new Vector3(0.2,  0.1,  0.2);

        ps.color1    = new Color4(0.4, 1.0, 1.0, 1);
        ps.color2    = new Color4(0.1, 0.5, 1.0, 1);
        ps.colorDead = new Color4(0.0, 0.2, 0.8, 0);

        ps.minSize     = 0.08;
        ps.maxSize     = 0.45;
        ps.minLifeTime = 0.08;
        ps.maxLifeTime = 0.3;

        // Direction opposée au dash = effet de propulsion
        const back = direction.negate().normalize();
        ps.direction1 = back.add(new Vector3(-0.6, 0.4, -0.6));
        ps.direction2 = back.add(new Vector3(0.6, 1.2, 0.6));
        ps.minEmitPower = 4;
        ps.maxEmitPower = 9;

        ps.emitRate            = 0;
        ps.manualEmitCount     = 100;
        ps.targetStopDuration  = 0.04;
        ps.gravity             = new Vector3(0, -6, 0);
        ps.start();
    }

    /** Étincelles blanches/jaunes quand le dash touche un ennemi sans le tuer */
    dashImpact(position) {
        const ps = this._makePS("dashImpact", 40);
        ps.emitter    = position.clone();
        ps.minEmitBox = new Vector3(-0.2, -0.2, -0.2);
        ps.maxEmitBox = new Vector3(0.2,  0.2,  0.2);

        ps.color1    = new Color4(1, 1, 0.6, 1);
        ps.color2    = new Color4(1, 0.8, 0.2, 1);
        ps.colorDead = new Color4(1, 0.5, 0,   0);

        ps.minSize     = 0.05;
        ps.maxSize     = 0.25;
        ps.minLifeTime = 0.05;
        ps.maxLifeTime = 0.2;

        ps.direction1   = new Vector3(-4, 2, -4);
        ps.direction2   = new Vector3(4,  5, 4);
        ps.minEmitPower = 2;
        ps.maxEmitPower = 5;

        ps.emitRate           = 0;
        ps.manualEmitCount    = 40;
        ps.targetStopDuration = 0.03;
        ps.gravity            = new Vector3(0, -10, 0);
        ps.start();
    }

    /** Grande explosion colorée à la mort d'un ennemi */
    enemyDeath(position, enemyType) {
        const base = ENEMY_COLORS[enemyType] || new Color4(1, 0.45, 0, 1);
        const dark = new Color4(base.r * 0.4, base.g * 0.4, base.b * 0.4, 1);
        const dead = new Color4(base.r * 0.1, base.g * 0.1, base.b * 0.1, 0);

        // Éclats principaux
        const ps = this._makePS("enemyDeath", 150);
        ps.emitter    = position.clone();
        ps.minEmitBox = new Vector3(-0.4, 0, -0.4);
        ps.maxEmitBox = new Vector3(0.4, 0.6, 0.4);

        ps.color1    = base;
        ps.color2    = dark;
        ps.colorDead = dead;

        ps.minSize     = 0.15;
        ps.maxSize     = 1.1;
        ps.minLifeTime = 0.3;
        ps.maxLifeTime = 0.85;

        ps.direction1   = new Vector3(-4, 5, -4);
        ps.direction2   = new Vector3(4, 10,  4);
        ps.minEmitPower = 3;
        ps.maxEmitPower = 8;

        ps.emitRate           = 0;
        ps.manualEmitCount    = 150;
        ps.targetStopDuration = 0.05;
        ps.gravity            = new Vector3(0, -9, 0);
        ps.start();

        // Anneau de poussière au sol (petites particules blanches qui s'étalent)
        const ring = this._makePS("enemyDeathRing", 60);
        ring.emitter    = new Vector3(position.x, position.y + 0.1, position.z);
        ring.minEmitBox = new Vector3(-0.1, 0, -0.1);
        ring.maxEmitBox = new Vector3(0.1, 0, 0.1);

        ring.color1    = new Color4(1, 1, 1, 0.8);
        ring.color2    = new Color4(base.r, base.g, base.b, 0.5);
        ring.colorDead = new Color4(0.5, 0.5, 0.5, 0);

        ring.minSize     = 0.05;
        ring.maxSize     = 0.2;
        ring.minLifeTime = 0.2;
        ring.maxLifeTime = 0.5;

        ring.direction1   = new Vector3(-5, 0.2, -5);
        ring.direction2   = new Vector3(5,  0.5,  5);
        ring.minEmitPower = 2;
        ring.maxEmitPower = 5;

        ring.emitRate           = 0;
        ring.manualEmitCount    = 60;
        ring.targetStopDuration = 0.03;
        ring.gravity            = new Vector3(0, -1, 0);
        ring.start();
    }

    /** Explosion + shockwave visuel pour la bombe du Bombardier */
    bombExplosion(position) {
        const ps = this._makePS("bombExplosion", 200);
        ps.emitter    = position.clone();
        ps.minEmitBox = new Vector3(-0.5, 0, -0.5);
        ps.maxEmitBox = new Vector3(0.5, 0.5, 0.5);

        ps.color1    = new Color4(1.0, 0.6, 0.1, 1);
        ps.color2    = new Color4(1.0, 0.2, 0.0, 1);
        ps.colorDead = new Color4(0.2, 0.1, 0.0, 0);

        ps.minSize     = 0.2;
        ps.maxSize     = 1.5;
        ps.minLifeTime = 0.3;
        ps.maxLifeTime = 1.0;

        ps.direction1   = new Vector3(-5, 4, -5);
        ps.direction2   = new Vector3(5, 12, 5);
        ps.minEmitPower = 3;
        ps.maxEmitPower = 10;

        ps.emitRate           = 0;
        ps.manualEmitCount    = 200;
        ps.targetStopDuration = 0.05;
        ps.gravity            = new Vector3(0, -10, 0);
        ps.start();
    }

    // ─── Interne ─────────────────────────────────────────────────────────────

    _makePS(name, capacity) {
        const ps           = new ParticleSystem(name, capacity, this.scene);
        ps.particleTexture = this._dotTex;
        ps.disposeOnStop   = true;
        ps.updateSpeed     = 0.02;
        return ps;
    }
}
